/**
 * The tabs of the practice settings page, and how to link to each one.
 *
 * Tools that need configuring (Scheduler, Payments) keep their settings here
 * rather than inside the tool, so everything a therapist configures sits in one
 * place. A tool's tab only exists once the therapist adopts that tool — the same
 * rule the sidebar follows — so nobody is asked to set up something they do not
 * use.
 */
export const PRACTICE_SETTINGS_PATH = "/settings";
export const PRACTICE_SETTINGS_TAB_PARAM = "tab";

export type ToolSettingsTab = "scheduler" | "payments";
export type PracticeSettingsTab = "language" | "templates" | "alerts" | ToolSettingsTab;

const PRACTICE_SETTINGS_TABS: readonly PracticeSettingsTab[] = [
    "language",
    "templates",
    "alerts",
    "scheduler",
    "payments",
];

/** Tool tabs in display order, with the marketplace slug that unlocks each. */
export const TOOL_SETTINGS_TABS: readonly { slug: string; tab: ToolSettingsTab }[] = [
    { slug: "scheduler", tab: "scheduler" },
    { slug: "payments", tab: "payments" },
];

/** The tab named in the URL, or null when it names nothing we know. */
export const parsePracticeSettingsTab = (
    value: string | null
): PracticeSettingsTab | null =>
    PRACTICE_SETTINGS_TABS.find((tab) => tab === value) ?? null;

export const isToolSettingsTab = (
    tab: PracticeSettingsTab
): tab is ToolSettingsTab => TOOL_SETTINGS_TABS.some((entry) => entry.tab === tab);

/** The tool tabs this therapist should see, in display order. */
export const visibleToolSettingsTabs = (
    adoptedSlugs: Iterable<string>
): ToolSettingsTab[] => {
    const adopted = new Set(adoptedSlugs);
    return TOOL_SETTINGS_TABS.filter((entry) => adopted.has(entry.slug)).map(
        (entry) => entry.tab
    );
};

/**
 * Address of one tab, e.g. `/settings?tab=scheduler&connected=zoom`.
 *
 * `params` ride along for whatever the tab reads on arrival; they can never
 * override which tab opens.
 */
export const practiceSettingsPath = (
    tab: PracticeSettingsTab,
    params: Record<string, string> = {}
): string => {
    const query = new URLSearchParams({ [PRACTICE_SETTINGS_TAB_PARAM]: tab });
    for (const [key, value] of Object.entries(params)) {
        if (key !== PRACTICE_SETTINGS_TAB_PARAM) query.set(key, value);
    }
    return `${PRACTICE_SETTINGS_PATH}?${query.toString()}`;
};
